import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import moment from "moment";
import { useSelector } from "react-redux";

import DashBoardNavbar from "../Components/DashBoardNavbar";
import HomePageNavbarFinancials from "../Components/HomePageNavbarFinancials";
import Loading from "../Components/Loading";
import LoadingSpinner from "../Components/Loader/UploadLoader";
import HomePage from "./HomePage";

import SHIFTQUERIES from "./CASHIER/SHIFTQUERIES";
import PosCashierUnpaidBills from "./CASHIER/PosCashierUnpaidBills";

const DAYENDPROCEDURE = () => {
  const BASEURL_GLOBAL = process.env.REACT_APP_BASEURL_GLOBAL;
  const BASEURL = process.env.REACT_APP_BASEURL;

  const authToken = window.sessionStorage.getItem("auth-token");
  const sessiontoken = window.sessionStorage.getItem("session-token");

  const userState = useSelector((state) => state.changeTheState.user);

  // ============================================================
  // HEADER
  // ============================================================

  const [dayDate, setDayDate] = useState(moment().format("YYYY-MM-DD"));

  const [showDayEndDetails, setShowDayEndDetails] = useState(false);
  const [startDatetime, setStartDatetime] = useState("");
  const [endDatetime, setEndDatetime] = useState(
    moment().format("YYYY-MM-DDTHH:mm"),
  );

  const [supervisor, setSupervisor] = useState(userState?.emailId || "");

  const [nextDaySupervisor, setNextDaySupervisor] = useState(
    userState?.emailId || "",
  );

  // ============================================================
  // SHIFT 1
  // ============================================================

  const [shift1, setShift1] = useState({
    openingCash: 0,
    cashSales: 0,
    mpesaSales: 0,
    creditCardSales: 0,
    creditSales: 0,
    totalReceived: 0,
    cashDrops: 0,
    closingCashFloat: 0,
    billsRaised: 0,
    voidedBills: 0,
    complimentaryBills: 0,
  });

  // ============================================================
  // SHIFT 2
  // ============================================================

  const [shift2, setShift2] = useState({
    openingCash: 0,
    cashSales: 0,
    mpesaSales: 0,
    creditCardSales: 0,
    creditSales: 0,
    totalReceived: 0,
    cashDrops: 0,
    closingCashFloat: 0,
    billsRaised: 0,
    voidedBills: 0,
    complimentaryBills: 0,
  });

  // ============================================================
  // LOADING
  // ============================================================

  const [loading, setLoading] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);

  // ============================================================
  // POPUPS
  // ============================================================

  const [showUnpaidPopup, setShowUnpaidPopup] = useState(false);
  const [showOpenShiftPopup, setShowOpenShiftPopup] = useState(false);

  const [unpaidBills, setUnpaidBills] = useState([]);

  // ============================================================
  // REPORT
  // ============================================================

  const [pdfData, setPdfData] = useState(null);
  const [showReportPopup, setShowReportPopup] = useState(false);

  const [company, setCompany] = useState({});

  // ============================================================
  // COMMON HEADERS
  // ============================================================

  const getHeaders = () => ({
    headers: {
      "auth-token": authToken,
      "session-token": sessiontoken,
    },
  });

  // ============================================================
  // EMPTY SHIFT OBJECT
  // ============================================================

  const emptyShift = () => ({
    openingCash: 0,
    cashSales: 0,
    mpesaSales: 0,
    creditCardSales: 0,
    creditSales: 0,
    totalReceived: 0,
    cashDrops: 0,
    closingCashFloat: 0,
    billsRaised: 0,
    voidedBills: 0,
    complimentaryBills: 0,
  });

  // ============================================================
  // NUMBER HELPER
  // ============================================================

  const numberValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return 0;
    }

    const number = Number(value);

    return Number.isFinite(number) ? number : 0;
  };

  // ============================================================
  // FORMAT AMOUNT
  // ============================================================

  const formatAmount = (value) => {
    return numberValue(value).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // ============================================================
  // MAP 1817 RESPONSE
  // ============================================================

  const mapShiftData = (data) => {
    if (!data) {
      return emptyShift();
    }

    return {
      openingCash: numberValue(
        data.OPENINGCASH ?? data.OPENINGCASHSALES ?? data.OPENINGFLOAT,
      ),

      cashSales: numberValue(
        data.CASHCOLLECTIONS ?? data.CASHSCOLLECTIONS ?? data.CASHSALES,
      ),

      mpesaSales: numberValue(data.MPESACOLLECTIONS ?? data.MPESASALES),

      creditCardSales: numberValue(
        data.CREDITCARDCOLLECTIONS ?? data.CREDITCARDSALES,
      ),

      creditSales: numberValue(data.CREDITSALES),

      totalReceived: numberValue(data.TOTALSALES ?? data.TOTALRECEIVED),

      cashDrops: numberValue(data.CASHDROPS),

      closingCashFloat: numberValue(data.CLOSINGFLOAT ?? data.CLOSINGCASHFLOAT),

      billsRaised: numberValue(data.CASHSALES ?? data.BILLSRAISED),

      voidedBills: numberValue(data.VOIDEDBILLS),

      complimentaryBills: numberValue(data.COMPLIMENTARYBILLS),
    };
  };

  // ============================================================
  // 1817
  // DAY END SHIFT DATA
  //
  // Documentation:
  // Object 1817 is called with built-in parameter 1 or 2
  // depending on Shift Number.
  //
  // If your backend expects another query parameter name,
  // change ONLY the URL below.
  // ============================================================

  const getDayEndShiftData = async (shiftNumber) => {
    try {
      const response = await axios.get(
        `${BASEURL_GLOBAL}/globalViewHandler?viewname=1817&SHIFTNO=${shiftNumber}`,
        getHeaders(),
      );

      const responseData = response?.data;

      const data = Array.isArray(responseData) ? responseData[0] : responseData;

      return data || null;
    } catch (error) {
      console.error(
        `Error fetching Day End Shift ${shiftNumber} from 1817:`,
        error,
      );
      setLoading(false);

      throw error;
    }
  };

  // ============================================================
  // LOAD DAY END DATA
  // ============================================================

  const getDayEndData = async () => {
    try {
      setShift1(emptyShift());
      setShift2(emptyShift());

      // --------------------------------------------------------
      // Object 1817 - Shift 1
      // --------------------------------------------------------

      const shift1Data = await getDayEndShiftData(1);

      // --------------------------------------------------------
      // Object 1817 - Shift 2
      // --------------------------------------------------------

      const shift2Data = await getDayEndShiftData(2);

      // --------------------------------------------------------
      // Set Shift Data
      // --------------------------------------------------------

      const mappedShift1 = mapShiftData(shift1Data);
      const mappedShift2 = mapShiftData(shift2Data);

      setShift1(mappedShift1);
      setShift2(mappedShift2);

      // --------------------------------------------------------
      // Day Start Datetime
      //
      // Prefer Shift 1 start date/time.
      // --------------------------------------------------------

      const dayStart =
        shift1Data?.STARTDATETIME ??
        shift1Data?.SHIFTSTARTDATETIME ??
        shift1Data?.DAYSTARTDATETIME ??
        shift2Data?.STARTDATETIME ??
        "";

      if (dayStart) {
        setStartDatetime(moment(dayStart).format("YYYY-MM-DDTHH:mm"));
      } else {
        setStartDatetime(
          moment(dayDate).startOf("day").format("YYYY-MM-DDTHH:mm"),
        );
      }

      // --------------------------------------------------------
      // End Datetime = Current Date/Time
      // --------------------------------------------------------

      setEndDatetime(moment().format("YYYY-MM-DDTHH:mm"));
    } catch (error) {
      console.error("Error loading Day End:", error);
      setLoading(false);
      alert(error?.response?.data?.message || "Unable to fetch Day End data");
    }
  };

  // ============================================================
  // DAY END TOTALS
  // ============================================================

  const dayEndTotals = useMemo(() => {
    return {
      openingCash: shift1.openingCash,

      cashSales: shift1.cashSales + shift2.cashSales,

      mpesaSales: shift1.mpesaSales + shift2.mpesaSales,

      creditCardSales: shift1.creditCardSales + shift2.creditCardSales,

      creditSales: shift1.creditSales + shift2.creditSales,

      totalReceived: shift1.totalReceived + shift2.totalReceived,

      cashDrops: shift1.cashDrops + shift2.cashDrops,

      closingCashFloat: shift2.closingCashFloat,

      billsRaised: shift1.billsRaised + shift2.billsRaised,

      voidedBills: shift1.voidedBills + shift2.voidedBills,

      complimentaryBills: shift1.complimentaryBills + shift2.complimentaryBills,
    };
  }, [shift1, shift2]);

  // ============================================================
  // 1680
  // CHECK UNPAID / PARTIALLY PAID BILLS
  // ============================================================

  const checkUnpaidBills = async () => {
    try {
      const response = await axios.get(
        `${BASEURL_GLOBAL}/globalViewHandler?viewname=1680`,
        getHeaders(),
      );

      const rows = Array.isArray(response?.data) ? response.data : [];

      // --------------------------------------------------------
      // Documentation screenshot shows PENDING column.
      //
      // Also support common backend field names so that the
      // validation does not depend on only one casing/name.
      // --------------------------------------------------------

      const pendingRows = rows.filter((item) => {
        const pending = numberValue(
          item?.PENDING ??
            item?.PENDINGAMOUNT ??
            item?.BALANCE ??
            item?.BALANCEAMOUNT,
        );

        const status = String(
          item?.STATUS ?? item?.PAYMENTSTATUS ?? "",
        ).toUpperCase();

        const isPendingStatus =
          status.includes("UNPAID") || status.includes("PARTIALLY");

        return pending > 0 || isPendingStatus;
      });

      return pendingRows;
    } catch (error) {
      console.error("Error checking unpaid/partially paid bills 1680:", error);
      setLoading(false);
      throw error;
    }
  };

  // ============================================================
  // 1810
  // CHECK OPEN SHIFTS
  // ============================================================

  const checkOpenShifts = async () => {
    try {
      const response = await axios.get(
        `${BASEURL_GLOBAL}/globalViewHandler?viewname=1810`,
        getHeaders(),
      );

      const rows = Array.isArray(response?.data) ? response.data : [];

      // --------------------------------------------------------
      // Documentation requires:
      // "There are shifts not closed"
      //
      // The screenshot shows STATUS = STARTED.
      // --------------------------------------------------------

      const openRows = rows.filter((item) => {
        const status = String(
          item?.STATUS ?? item?.SHIFTSTATUS ?? "",
        ).toUpperCase();

        return status === "STARTED" || status === "OPEN" || status === "START";
      });

      return openRows;
    } catch (error) {
      console.error("Error checking open shifts 1810:", error);

      throw error;
    }
  };

  // ============================================================
  // CLOSE DAY - SP 1812
  // ============================================================

  const handleCloseDay = async () => {
    // ========================================================
    // BASIC VALIDATION
    // ========================================================

    if (!dayDate) {
      alert("Date is required");
      return;
    }

    if (!supervisor) {
      alert("Supervisor is required");
      return;
    }

    if (!nextDaySupervisor) {
      alert("Next Day Supervisor is required");
      return;
    }

    try {
      const response = await axios.get(
        `${BASEURL_GLOBAL}/globalViewHandler?viewname=1816`,
        getHeaders(),
      );

      console.log(response.data);
      const filterData = response?.data?.filter(
        (item) =>
          moment(item?.DATE).format("DD/MM/YYYY") ===
          moment(dayDate).format("DD/MM/YYYY"),
      );

      console.log(filterData);

      if (filterData?.[0]?.STATUS === "ENDED") {
        alert("Day is already closed");
        return;
      }
    } catch (error) {
      console.error("Error fetching closed shifts:", error);
    }

    // ========================================================
    // CONFIRMATION
    // ========================================================

    const confirmClose = window.confirm(
      "Are you sure you want to Close the Day?",
    );

    if (!confirmClose) {
      return;
    }

    try {
      setLoading(true);

      // ======================================================
      // STEP 1
      // CHECK UNPAID / PARTIALLY PAID BILLS - OBJECT 1680
      // ======================================================

      const unpaidRows = await checkUnpaidBills();

      if (unpaidRows.length > 0) {
        setUnpaidBills(unpaidRows);
        setShowUnpaidPopup(true);

        alert("There are bills partially paid or unpaid");

        return;
      }

      // ======================================================
      // STEP 2
      // CHECK OPEN SHIFTS - OBJECT 1810
      // ======================================================

      const openShiftRows = await checkOpenShifts();

      if (openShiftRows.length > 0) {
        setShowOpenShiftPopup(true);

        alert("There are shifts not closed");

        return;
      }

      // ======================================================
      // STEP 3
      // BOTH CONDITIONS PASSED
      // CALL SP 1812
      // ======================================================

      const payload = {
        // @CLOSINGFLOAT
        CLOSINGFLOAT: Number(dayEndTotals.closingCashFloat || 0),

        // @DATE
        DATE: moment(dayDate).format("YYYY/MM/DD"),

        // @MPESACOLLECTIONS
        MPESACOLLECTIONS: Number(dayEndTotals.mpesaSales || 0),

        // @CASHCOLLECTIONS
        CASHCOLLECTIONS: Number(dayEndTotals.cashSales || 0),

        // @OTHERSCOLLECTION
        //
        // No separate "Other Sales" column exists in the
        // documented Day End table.
        //
        // Therefore use the difference between Total Received
        // and the documented payment collections.
        OTHERSCOLLECTION:
          Number(
            dayEndTotals.totalReceived -
              dayEndTotals.cashSales -
              dayEndTotals.mpesaSales -
              dayEndTotals.creditCardSales -
              dayEndTotals.creditSales,
          ) || 0,

        // @CASHSALES
        CASHSALES: Number(dayEndTotals.billsRaised || 0),

        // @CREDITSALES
        CREDITSALES: Number(dayEndTotals.creditSales || 0),

        // @CREDITCARDCOLLECTIONS
        CREDITCARDCOLLECTIONS: Number(dayEndTotals.creditCardSales || 0),

        // @VOIDEDBILLS
        VOIDEDBILLS: Number(dayEndTotals.voidedBills || 0),

        // @COMPLIMENTARYBILLS
        COMPLIMENTARYBILLS: Number(dayEndTotals.complimentaryBills || 0),

        // @PHYSICALCASHINHAND
        //
        // Documentation says:
        // Day End Total Column
        //
        // Therefore use Day End Closing Cash Float.
        PHYSICALCASHINHAND: Number(dayEndTotals.closingCashFloat || 0),

        // @NEWDAYEMPLOYEE
        NEWDAYEMPLOYEE: nextDaySupervisor || "",

        // @DAYENDDATETIME
        DAYENDDATETIME: moment().format("YYYY-MM-DD HH:mm:ss"),

        // @CLOSEDBY
        CLOSEDBY: supervisor?.split("@")?.[0]?.toUpperCase() || "",
      };

      console.log("SP 1812 Payload:", payload);

      // ======================================================
      // SP 1812
      // ======================================================

      const response = await axios.post(
        `${BASEURL_GLOBAL}/globalSpHandler?spname=1812`,
        payload,
        getHeaders(),
      );

      if (response?.status === 201) {
        alert("Day Closed Successfully");

        const isPrint = window.confirm("Do you want to print this Receipt?");
        if (isPrint) {
          setShowReportPopup(true);
          generateDayEndReport();
        } else {
          alert("You are now entering a new shift");
          setEndDatetime(moment().format("YYYY-MM-DDTHH:mm"));
          await getDayEndData();
        }
      } else {
        alert("Unable to Close Day");
      }
    } catch (error) {
      console.error("Error while closing Day:", error);

      alert(
        error?.response?.data?.message ||
          "Something went wrong while closing the Day",
      );
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // REPORT
  // ============================================================

  const generateDayEndReport = async () => {
    try {
      setPdfData(null);
      setShowReportPopup(true);
      setLoadingReport(true);

      const response = await axios.post(
        `${BASEURL}/generatereport`,
        {
          REPORTNAME: "CrPOSDayEndReport.rpt",
          REPORTPATH: company?.REPORTPATH,
          DIVISIONBASED: "NO",
          DIVISIONNAME: "",
          SINGLEMULTIDATE: "",
          STARTDATE: "",
          ENDDATE: moment(dayDate).format("YYYY-MM-DD"),
        },
        {
          headers: {
            "auth-token": authToken,
            "session-token": sessiontoken,
          },
          responseType: "blob",
        },
      );

      const file = new Blob([response.data], {
        type: "application/pdf",
      });

      const fileURL = URL.createObjectURL(file);

      setPdfData(fileURL);
    } catch (error) {
      console.error("Error generating Day End report:", error);

      alert("Unable to generate Day End Report");
    } finally {
      setLoadingReport(false);
    }
  };

  // ============================================================
  // DOWNLOAD REPORT
  // ============================================================

  const handleDownload = () => {
    if (!pdfData) {
      return;
    }

    const a = document.createElement("a");

    a.href = pdfData;
    a.download = "Day_End_Report.pdf";

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // ============================================================
  // COMPANY
  // ============================================================

  const getCompany = async () => {
    try {
      const response = await axios.get(
        `${BASEURL_GLOBAL}/globalViewHandler?viewname=602`,
        getHeaders(),
      );

      setCompany(response?.data?.[0] || {});
    } catch (error) {
      console.error("Error fetching company:", error);
    }
  };

  // ============================================================
  // INITIAL LOAD
  // ============================================================

  useEffect(() => {
    const initialize = async () => {
      setEndDatetime(moment().format("YYYY-MM-DDTHH:mm"));

      await getCompany();
      await getDayEndData();
      setLoading(false);
    };

    initialize();
  }, []);

  // ============================================================
  // KEEP CURRENT USER AS DEFAULT SUPERVISOR
  // ============================================================

  useEffect(() => {
    if (userState?.emailId) {
      setSupervisor(userState.emailId);

      if (!nextDaySupervisor) {
        setNextDaySupervisor(userState.emailId);
      }
    }
  }, [userState?.emailId]);

  // ============================================================
  // DAY DATE CHANGE
  // ============================================================

  const handleDateChange = async (e) => {
    const value = e.target.value;

    setDayDate(value);

    if (!value) {
      return;
    }

    setEndDatetime(moment().format("YYYY-MM-DDTHH:mm"));

    await getDayEndData();
  };

  // ============================================================
  // POPUP ROW VALUE
  // ============================================================

  const getValue = (row, ...keys) => {
    for (const key of keys) {
      if (row?.[key] !== undefined && row?.[key] !== null) {
        return row[key];
      }
    }

    return "";
  };

  // ============================================================
  // LOADING
  // ============================================================

  if (loading) {
    return <Loading />;
  }

  // ============================================================
  // UI
  // ============================================================

  return (
    <div className="min-h-screen w-full bg-white text-[12px]">
      <DashBoardNavbar />
      <HomePageNavbarFinancials />

      {/* ======================================================
          HEADER
      ====================================================== */}

      <div className="mx-auto mt-2 w-[95%] max-w-[1200px]">
        <div className="overflow-x-auto">
          <table className="border-collapse border border-black">
            <thead>
              <tr className="bg-[#002d62] text-white">
                <th className="border border-black px-3 py-1 text-left font-normal">
                  Date
                </th>

                <th className="border border-black px-3 py-1 text-left font-normal">
                  Start Datetime
                </th>

                <th className="border border-black px-3 py-1 text-left font-normal">
                  End Datetime
                </th>

                <th className="border border-black px-3 py-1 text-left font-normal">
                  Supervisor
                </th>
              </tr>
            </thead>

            <tbody>
              <tr>
                {/* DATE */}
                <td className="border border-black px-2 py-1">
                  <input
                    type="date"
                    value={dayDate}
                    onChange={handleDateChange}
                    className="h-7 w-[125px] border border-gray-400 px-1"
                  />
                </td>

                {/* START DATETIME */}
                <td className="border border-black px-2 py-1">
                  <input
                    type="datetime-local"
                    value={startDatetime}
                    readOnly
                    className="h-7 w-[185px] border border-gray-400 bg-gray-100 px-1"
                  />
                </td>

                {/* END DATETIME */}
                <td className="border border-black px-2 py-1">
                  <input
                    type="datetime-local"
                    value={endDatetime}
                    readOnly
                    className="h-7 w-[185px] border border-gray-400 bg-gray-100 px-1"
                  />
                </td>

                {/* SUPERVISOR */}
                <td className="border border-black px-2 py-1">
                  <select
                    value={supervisor}
                    onChange={(e) => setSupervisor(e.target.value)}
                    className="h-7 w-[220px] border border-gray-400 bg-white px-1"
                  >
                    <option value="">Select Supervisor</option>

                    {userState?.emailId && (
                      <option value={userState.emailId}>
                        {userState.emailId}
                      </option>
                    )}
                  </select>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* ====================================================
            NEXT DAY SUPERVISOR
        ==================================================== */}

        <div className="mt-2 flex items-center gap-2">
          <label className="font-semibold">Next Day Supervisor</label>

          <select
            value={nextDaySupervisor}
            onChange={(e) => setNextDaySupervisor(e.target.value)}
            className="h-7 w-[220px] border border-gray-400 bg-white px-1"
          >
            <option value="">Select Supervisor</option>

            {userState?.emailId && (
              <option value={userState.emailId}>{userState.emailId}</option>
            )}
          </select>
        </div>

        {/* ====================================================
            DAY END SUMMARIES
        ==================================================== */}

        <h2 className="mt-3 text-[16px] font-bold">Day End Summaries</h2>

        <div className="mt-1 overflow-x-auto">
          <table className="w-full max-w-[900px] border-collapse border border-black">
            <thead>
              <tr className="bg-[#002d62] text-white">
                <th className="w-[220px] border border-black px-2 py-1 text-left">
                  Summary
                </th>

                <th className="border border-black px-2 py-1 text-right">
                  Shift 1 Value
                </th>

                <th className="border border-black px-2 py-1 text-right">
                  Shift 2 Value
                </th>

                <th className="border border-black px-2 py-1 text-right">
                  Day end Total
                </th>
              </tr>
            </thead>

            <tbody>
              {/* ==================================================
                  OPENING CASH
              ================================================== */}

              <tr>
                <td className="border border-black px-2 py-1">Opening Cash</td>

                <td className="border border-black px-2 py-1 text-right">
                  {formatAmount(shift1.openingCash)}
                </td>

                <td className="border border-black px-2 py-1 text-right">
                  {formatAmount(shift2.openingCash)}
                </td>

                <td className="border border-black px-2 py-1 text-right">
                  {formatAmount(dayEndTotals.openingCash)}
                </td>
              </tr>

              {/* CASH SALES */}

              <tr>
                <td className="border border-black px-2 py-1">Cash Sales</td>

                <td className="border border-black px-2 py-1 text-right">
                  {formatAmount(shift1.cashSales)}
                </td>

                <td className="border border-black px-2 py-1 text-right">
                  {formatAmount(shift2.cashSales)}
                </td>

                <td className="border border-black px-2 py-1 text-right">
                  {formatAmount(dayEndTotals.cashSales)}
                </td>
              </tr>

              {/* MPESA SALES */}

              <tr>
                <td className="border border-black px-2 py-1">Mpesa Sales</td>

                <td className="border border-black px-2 py-1 text-right">
                  {formatAmount(shift1.mpesaSales)}
                </td>

                <td className="border border-black px-2 py-1 text-right">
                  {formatAmount(shift2.mpesaSales)}
                </td>

                <td className="border border-black px-2 py-1 text-right">
                  {formatAmount(dayEndTotals.mpesaSales)}
                </td>
              </tr>

              {/* CREDIT CARD SALES */}

              <tr>
                <td className="border border-black px-2 py-1">
                  Credit Card Sales
                </td>

                <td className="border border-black px-2 py-1 text-right">
                  {formatAmount(shift1.creditCardSales)}
                </td>

                <td className="border border-black px-2 py-1 text-right">
                  {formatAmount(shift2.creditCardSales)}
                </td>

                <td className="border border-black px-2 py-1 text-right">
                  {formatAmount(dayEndTotals.creditCardSales)}
                </td>
              </tr>

              {/* CREDIT SALES */}

              <tr>
                <td className="border border-black px-2 py-1">Credit Sales</td>

                <td className="border border-black px-2 py-1 text-right">
                  {formatAmount(shift1.creditSales)}
                </td>

                <td className="border border-black px-2 py-1 text-right">
                  {formatAmount(shift2.creditSales)}
                </td>

                <td className="border border-black px-2 py-1 text-right">
                  {formatAmount(dayEndTotals.creditSales)}
                </td>
              </tr>

              {/* TOTAL RECEIVED */}

              <tr>
                <td className="border border-black px-2 py-1">
                  Total Received
                </td>

                <td className="border border-black px-2 py-1 text-right">
                  {formatAmount(shift1.totalReceived)}
                </td>

                <td className="border border-black px-2 py-1 text-right">
                  {formatAmount(shift2.totalReceived)}
                </td>

                <td className="border border-black px-2 py-1 text-right font-semibold">
                  {formatAmount(dayEndTotals.totalReceived)}
                </td>
              </tr>

              {/* CASH DROPS */}

              <tr>
                <td className="border border-black px-2 py-1">Cash Drops</td>

                <td className="border border-black px-2 py-1 text-right">
                  {formatAmount(shift1.cashDrops)}
                </td>

                <td className="border border-black px-2 py-1 text-right">
                  {formatAmount(shift2.cashDrops)}
                </td>

                <td className="border border-black px-2 py-1 text-right">
                  {formatAmount(dayEndTotals.cashDrops)}
                </td>
              </tr>

              {/* CLOSING CASH FLOAT */}

              <tr>
                <td className="border border-black px-2 py-1">
                  Closing Cash Float
                </td>

                <td className="border border-black px-2 py-1 text-right">
                  {formatAmount(shift1.closingCashFloat)}
                </td>

                <td className="border border-black px-2 py-1 text-right">
                  {formatAmount(shift2.closingCashFloat)}
                </td>

                <td className="border border-black px-2 py-1 text-right font-semibold">
                  {formatAmount(dayEndTotals.closingCashFloat)}
                </td>
              </tr>

              {/* ==================================================
                  BILLS RAISED
              ================================================== */}

              <tr>
                <td className="border border-black px-2 py-1">Bills Raised</td>

                <td className="border border-black bg-gray-300 px-2 py-1"></td>

                <td className="border border-black px-2 py-1 text-right">
                  {formatAmount(shift2.billsRaised)}
                </td>

                <td className="border border-black px-2 py-1 text-right">
                  {formatAmount(dayEndTotals.billsRaised)}
                </td>
              </tr>

              {/* VOIDED BILLS */}

              <tr>
                <td className="border border-black px-2 py-1">Voided Bills</td>

                <td className="border border-black bg-gray-300 px-2 py-1"></td>

                <td className="border border-black px-2 py-1 text-right">
                  {formatAmount(shift2.voidedBills)}
                </td>

                <td className="border border-black px-2 py-1 text-right">
                  {formatAmount(dayEndTotals.voidedBills)}
                </td>
              </tr>

              {/* COMPLIMENTARY BILLS */}

              <tr>
                <td className="border border-black px-2 py-1">
                  Complimentary Bills
                </td>

                <td className="border border-black bg-gray-300 px-2 py-1"></td>

                <td className="border border-black px-2 py-1 text-right">
                  {formatAmount(shift2.complimentaryBills)}
                </td>

                <td className="border border-black px-2 py-1 text-right">
                  {formatAmount(dayEndTotals.complimentaryBills)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* HomePage */}
        {showDayEndDetails && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20 p-2">
            <div className="relative bg-white border border-black h-[90vh] lg:h-[calc(100vh-90px)] w-full lg:w-[1300px] p-1 overflow-y-auto rounded-md shadow-xl">
              <HomePage hideNavBar={true} />
              <button
                className="absolute top-1 right-1 flex items-center justify-center border border-black bg-[#002d62] text-white rounded-md px-3 py-1 focus:outline-none focus:ring focus:border-blue-300"
                onClick={() => setShowDayEndDetails(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* ====================================================
            BUTTONS
        ==================================================== */}

        <div className="mt-5 flex flex-wrap gap-3">
          {/* VIEW DETAILS */}

          <button
            type="button"
            onClick={() => {
              setShowDayEndDetails(true);
            }}
            className="h-9 border border-black bg-[#426bb0] px-5 text-white hover:bg-[#315b9b]"
          >
            View Day end Details
          </button>

          {/* PRINT */}

          <button
            type="button"
            onClick={generateDayEndReport}
            className="h-9 border border-black bg-[#426bb0] px-5 text-white hover:bg-[#315b9b]"
          >
            Print Day end Report
          </button>

          {/* NEXT DAY SUPERVISOR */}

          {/* <button
            type="button"
            onClick={() => {
              document
                .querySelector("select[name='nextDaySupervisor']")
                ?.focus();
            }}
            className="h-9 border border-black bg-[#426bb0] px-5 text-white hover:bg-[#315b9b]"
          >
            Next Day Supervisor
          </button> */}

          {/* CLOSE DAY */}

          <button
            type="button"
            onClick={handleCloseDay}
            className="h-9 border border-black bg-[#426bb0] px-6 font-semibold text-white hover:bg-[#315b9b]"
          >
            Close Day
          </button>
        </div>
      </div>

      {/* ======================================================
          UNPAID / PARTIALLY PAID BILLS POPUP
      ====================================================== */}

      {showUnpaidPopup && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20 p-2">
          <div className="relative bg-white border border-black h-[90vh] lg:h-[calc(100vh-90px)] w-full lg:w-[1300px] p-1 overflow-y-auto rounded-md shadow-xl">
            <PosCashierUnpaidBills hideNavBar={true} />
            <button
              className="absolute top-1 right-1 flex items-center justify-center border border-black bg-[#002d62] text-white rounded-md px-3 py-1 focus:outline-none focus:ring focus:border-blue-300"
              onClick={() => setShowUnpaidPopup(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* ======================================================
          OPEN SHIFT POPUP
      ====================================================== */}

      {showOpenShiftPopup && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20 p-2">
          <div className="relative bg-white border border-black h-[90vh] lg:h-[calc(100vh-90px)] w-full lg:w-[1300px] p-1 overflow-y-auto rounded-md shadow-xl">
            <SHIFTQUERIES hideNavBar={true} />
            <button
              className="absolute top-1 right-1 flex items-center justify-center border border-black bg-[#002d62] text-white rounded-md px-3 py-1 focus:outline-none focus:ring focus:border-blue-300"
              onClick={() => setShowOpenShiftPopup(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* ======================================================
          REPORT POPUP
      ====================================================== */}
      {showReportPopup && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 p-4">
          <div className="relative h-[85vh] w-[90vw] max-w-[1200px] overflow-auto rounded-md border border-black bg-white p-4 shadow-xl">
            <button
              type="button"
              onClick={async () => {
                setShowReportPopup(false);
                alert("You are now entering a new shift");
                setEndDatetime(moment().format("YYYY-MM-DDTHH:mm"));
                await getDayEndData();
              }}
              className="absolute right-3 top-3 rounded-md border border-black bg-[#002d62] px-3 py-1 text-white"
            >
              Close
            </button>

            {loadingReport && <LoadingSpinner />}

            <div className="mt-8">
              {pdfData ? (
                <>
                  <div className="mb-3 flex items-center justify-between">
                    <h2 className="text-lg font-bold">Day End Report</h2>

                    <button
                      type="button"
                      onClick={handleDownload}
                      className="rounded-md bg-[#002d62] px-4 py-1.5 text-white"
                    >
                      Download PDF
                    </button>
                  </div>

                  <iframe
                    src={pdfData}
                    title="Day End Report"
                    className="h-[70vh] w-full border border-gray-400"
                  />
                </>
              ) : !loadingReport ? (
                <p className="py-10 text-center text-xl font-bold">
                  No report available.
                </p>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DAYENDPROCEDURE;
